/**
 * Custom React hook for Database Assistant
 * 
 * Manages natural language database queries, example questions,
 * conversation context via session IDs, and query history
 * with built-in loading and error handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import databaseAssistantService from '../services/databaseAssistantService';
import { useAccessToken } from './useAuth';
import { useAlert } from '../context/AlertContext';

const HISTORY_KEY = 'dbAssistantHistory';
const FAVORITES_KEY = 'dbAssistantFavorites';
const MAX_HISTORY = 20;

/**
 * Hook for Database Assistant functionality
 * @returns {Object} Database assistant methods and state
 */
export function useDatabaseAssistant() {
    const [response, setResponse] = useState(null);
    const [examples, setExamples] = useState(null);
    const [history, setHistory] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
    const [pendingQueryId, setPendingQueryId] = useState(null);
    const [clarificationQuestion, setClarificationQuestion] = useState(null);
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();

    // Generate a new session ID on mount
    useEffect(() => {
        const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        setSessionId(newSessionId);
    }, []);

    /**
     * Load history and favorites from localStorage on mount
     */
    useEffect(() => {
        try {
            const savedHistory = localStorage.getItem(HISTORY_KEY);
            if (savedHistory) {
                setHistory(JSON.parse(savedHistory));
            }

            const savedFavorites = localStorage.getItem(FAVORITES_KEY);
            if (savedFavorites) {
                setFavorites(JSON.parse(savedFavorites));
            }
        } catch (err) {
            console.error('Failed to load saved data:', err);
        }
    }, []);

    /**
     * Save to history
     */
    const saveToHistory = useCallback((question, answer) => {
        const newEntry = {
            question,
            answer,
            timestamp: new Date().toISOString()
        };

        setHistory((prev) => {
            const updated = [newEntry, ...prev.slice(0, MAX_HISTORY - 1)];
            localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
            return updated;
        });
    }, []);

    /**
     * Clear history
     */
    const clearHistory = useCallback(() => {
        setHistory([]);
        localStorage.removeItem(HISTORY_KEY);
        setInfoAlert('Query history cleared');
    }, [setInfoAlert]);

    /**
     * Add question to favorites
     */
    const addToFavorites = useCallback((question) => {
        if (!question || !question.trim()) return;

        setFavorites((prev) => {
            // Avoid duplicates
            if (prev.includes(question)) {
                setErrorAlert('Question already in favorites');
                return prev;
            }

            const updated = [...prev, question.trim()];
            localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
            setInfoAlert('Added to favorites');
            return updated;
        });
    }, [setInfoAlert, setErrorAlert]);

    /**
     * Remove question from favorites
     */
    const removeFromFavorites = useCallback((question) => {
        setFavorites((prev) => {
            const updated = prev.filter(q => q !== question);
            localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
            setInfoAlert('Removed from favorites');
            return updated;
        });
    }, [setInfoAlert]);

    /**
     * Clear favorites
     */
    const clearFavorites = useCallback(() => {
        setFavorites([]);
        localStorage.removeItem(FAVORITES_KEY);
        setInfoAlert('Favorites cleared');
    }, [setInfoAlert]);

    /**
     * Ask a database question
     * @param {string} question - The question to ask
     * @param {boolean} skipConfirmation - Set to true to bypass clarification
     * @param {string} pendingId - ID of pending query when confirming
     */
    const askQuestion = useCallback(async (question, skipConfirmation = false, pendingId = null) => {
        if (!question || !question.trim()) {
            setErrorAlert('Please enter a question');
            return null;
        }

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const accessToken = await getBackendAccessToken();
            const data = await databaseAssistantService.askQuestion(
                accessToken, 
                question,
                sessionId,  // Include sessionId for conversation context
                skipConfirmation,
                pendingId || pendingQueryId  // Use provided or stored pendingQueryId
            );
            
            // Attach the original question to the response
            const responseWithQuestion = {
                ...data,
                question: question.trim()
            };
            
            setResponse(responseWithQuestion);

            // Check if clarification is needed
            if (data.needsConfirmation && data.pendingQueryId) {
                setAwaitingConfirmation(true);
                setPendingQueryId(data.pendingQueryId);
                setClarificationQuestion(question.trim());
            } else {
                setAwaitingConfirmation(false);
                setPendingQueryId(null);
                setClarificationQuestion(null);
            }

            // Save to history if successful and not awaiting confirmation
            if (data.success && data.answer && !data.needsConfirmation) {
                saveToHistory(question.trim(), data.answer);
            }

            return responseWithQuestion;
        } catch (err) {
            let errorMessage = err instanceof Error ? err.message : 'Failed to process question';
            
            // Clean up error message - remove "An unexpected error occurred" suffix
            errorMessage = errorMessage.replace(/[;,]?\s*An unexpected error occurred\.?$/i, '');
            
            // Determine if this is a connection/network error or an assistant response error
            // Assistant response errors include: RESTRICTED, UNCLEAR_QUESTION, validation errors, etc.
            const isAssistantError = 
                errorMessage.includes('RESTRICTED:') ||
                errorMessage.includes('UNCLEAR_QUESTION:') ||
                err?.response?.status === 400; // Bad request (usually assistant validation)
            
            const isConnectionError = 
                !isAssistantError && (
                    errorMessage.toLowerCase().includes('network') ||
                    errorMessage.toLowerCase().includes('connection') ||
                    errorMessage.toLowerCase().includes('timeout') ||
                    errorMessage.toLowerCase().includes('failed to fetch') ||
                    err?.response?.status === 0 || // Network failure
                    err?.response?.status >= 500   // Server errors
                );
            
            // Only show alert for connection errors, not assistant response errors (like RESTRICTED)
            if (isConnectionError) {
                setErrorAlert(errorMessage);
            }
            
            setError(errorMessage);
            
            // Set error response with question
            const errorResponse = {
                success: false,
                answer: null,
                sqlQuery: null,
                queryResult: null,
                error: errorMessage,
                question: question.trim()
            };
            
            setResponse(errorResponse);
            
            // Reset clarification state on error
            setAwaitingConfirmation(false);
            setPendingQueryId(null);
            setClarificationQuestion(null);
            
            return errorResponse;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert, saveToHistory, sessionId, pendingQueryId]);

    /**
     * Fetch example questions
     */
    const fetchExamples = useCallback(async () => {
        try {
            const accessToken = await getBackendAccessToken();
            const data = await databaseAssistantService.getExamples(accessToken);
            setExamples(data);
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch examples';
            console.error(errorMessage);
            // Don't show error alert for examples - it's not critical
            throw err;
        }
    }, [getBackendAccessToken]);

    /**
     * Clear schema cache (Admin only)
     */
    const clearCache = useCallback(async () => {
        try {
            const accessToken = await getBackendAccessToken();
            await databaseAssistantService.clearCache(accessToken);
            setInfoAlert('Schema cache cleared successfully');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to clear cache';
            setErrorAlert(errorMessage);
            throw err;
        }
    }, [getBackendAccessToken, setInfoAlert, setErrorAlert]);

    /**
     * Confirm a clarification request
     */
    const handleConfirm = useCallback(async () => {
        if (!pendingQueryId) {
            setErrorAlert('No pending clarification to confirm');
            return null;
        }
        return await askQuestion('yes, that is correct', false, pendingQueryId);
    }, [pendingQueryId, askQuestion, setErrorAlert]);

    /**
     * Decline a clarification request
     */
    const handleDecline = useCallback(async () => {
        if (!pendingQueryId) {
            setErrorAlert('No pending clarification to decline');
            return null;
        }
        
        // Ask with decline phrase to clear pending query
        const result = await askQuestion('no, that is not correct', false, pendingQueryId);
        
        // Reset clarification state
        setAwaitingConfirmation(false);
        setPendingQueryId(null);
        setClarificationQuestion(null);
        
        return result;
    }, [pendingQueryId, askQuestion, setErrorAlert]);

    /**
     * Clear conversation history for current session
     * Generates a new session ID for a fresh conversation  
     */
    const clearConversation = useCallback(async () => {
        if (!sessionId) {
            setErrorAlert('No active session to clear');
            return;
        }

        try {
            const accessToken = await getBackendAccessToken();
            await databaseAssistantService.clearConversation(accessToken, sessionId);
            
            // Generate new session ID for fresh conversation
            const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            setSessionId(newSessionId);
            
            // Reset clarification state
            setAwaitingConfirmation(false);
            setPendingQueryId(null);
            setClarificationQuestion(null);
            
            setInfoAlert('Conversation cleared - starting fresh!');
        } catch (err) {
            let errorMessage = err instanceof Error ? err.message : 'Failed to clear conversation';
            
            // Don't show alert for JSON parsing errors (backend returns plain text)
            const isJsonParsingError = errorMessage.toLowerCase().includes('not valid json');
            
            if (!isJsonParsingError) {
                setErrorAlert(errorMessage);
            }
            
            // Still generate new session ID even if there was an error
            const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            setSessionId(newSessionId);
            
            throw err;
        }
    }, [sessionId, getBackendAccessToken, setInfoAlert, setErrorAlert]);

    /**
     * Clear all conversation histories (Admin only)
     */
    const clearAllConversations = useCallback(async () => {
        try {
            const accessToken = await getBackendAccessToken();
            await databaseAssistantService.clearAllConversations(accessToken);
            
            // Generate new session ID for current user
            const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            setSessionId(newSessionId);
            
            setInfoAlert('All conversations cleared');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to clear all conversations';
            setErrorAlert(errorMessage);
            throw err;
        }
    }, [getBackendAccessToken, setInfoAlert, setErrorAlert]);

    /**
     * Export query results to CSV
     */
    const exportToCSV = useCallback((queryResult, filename = 'query-results') => {
        if (!queryResult || queryResult.rowCount === 0) {
            setErrorAlert('No results to export');
            return;
        }

        try {
            // Build CSV content
            const headers = queryResult.columns.join(',');
            const rows = queryResult.rows.map(row =>
                queryResult.columns.map(col => {
                    const value = row[col] ?? '';
                    // Escape quotes and wrap in quotes if contains comma
                    const escaped = String(value).replace(/"/g, '""');
                    return escaped.includes(',') ? `"${escaped}"` : escaped;
                }).join(',')
            ).join('\n');

            const csv = `${headers}\n${rows}`;

            // Download
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}-${Date.now()}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);

            setInfoAlert('Results exported to CSV');
        } catch (err) {
            setErrorAlert('Failed to export results');
            console.error('CSV export error:', err);
        }
    }, [setInfoAlert, setErrorAlert]);

    /**
     * Copy text to clipboard
     */
    const copyToClipboard = useCallback(async (text, label = 'Text') => {
        if (!text) {
            setErrorAlert('Nothing to copy');
            return;
        }

        try {
            // Try modern Clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                setInfoAlert(`${label} copied to clipboard`);
            } else {
                // Fallback for older browsers or non-secure contexts
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    const successful = document.execCommand('copy');
                    if (successful) {
                        setInfoAlert(`${label} copied to clipboard`);
                    } else {
                        throw new Error('Copy command failed');
                    }
                } finally {
                    document.body.removeChild(textArea);
                }
            }
        } catch (err) {
            setErrorAlert('Failed to copy to clipboard');
            console.error('Clipboard error:', err);
        }
    }, [setInfoAlert, setErrorAlert]);

    /**
     * Detect if an error is a workflow security block
     */
    const isSecurityBlock = useCallback((errorMessage) => {
        if (!errorMessage) return false;
        const securityKeywords = [
            'restricted',
            'not permitted',
            'guardrail',
            'personnel',
            'audit',
            'secure token',
            'encrypted',
            'user activity',
            'blocked'
        ];
        const lowerError = errorMessage.toLowerCase();
        return securityKeywords.some(keyword => lowerError.includes(keyword));
    }, []);

    return {
        // State
        response,
        examples,
        history,
        favorites,
        loading,
        error,
        sessionId,
        awaitingConfirmation,
        pendingQueryId,
        clarificationQuestion,

        // Methods
        askQuestion,
        fetchExamples,
        clearCache,
        exportToCSV,
        copyToClipboard,
        isSecurityBlock,
        handleConfirm,
        handleDecline,

        // Conversation methods
        clearConversation,
        clearAllConversations,

        // History methods
        clearHistory,

        // Favorites methods
        addToFavorites,
        removeFromFavorites,
        clearFavorites
    };
}

export default useDatabaseAssistant;
